import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getMyProfile, updateMyProfile, changePassword } from '../../api/users';
import { useAuthStore } from '../../stores/authStore';
import { toast } from '../../stores/uiStore';
import type { UserProfile } from '../../api/users';
import styles from './SettingsPage.module.css';

const LOCALE_OPTIONS = [
  { value: 'fr', label: 'Français' },
  { value: 'de', label: 'Deutsch' },
  { value: 'it', label: 'Italiano' },
  { value: 'en', label: 'English' },
];

export function SettingsPage() {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const updateUser = useAuthStore((s) => s.updateUser);

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  // Profile form
  const [fullName, setFullName] = useState('');
  const [locale, setLocale] = useState('fr');
  const [saving, setSaving] = useState(false);

  // Password form
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [changingPw, setChangingPw] = useState(false);

  useEffect(() => {
    loadProfile();
  }, []);

  async function loadProfile() {
    try {
      setLoading(true);
      const data = await getMyProfile();
      setProfile(data);
      setFullName(data.full_name);
      setLocale(data.preferred_locale);
    } catch {
      toast.error(t('failedToLoadProfile'));
    } finally {
      setLoading(false);
    }
  }

  async function handleProfileSave(e: React.FormEvent) {
    e.preventDefault();
    try {
      setSaving(true);
      const updated = await updateMyProfile({
        full_name: fullName,
        preferred_locale: locale,
      });
      setProfile(updated);
      updateUser({ full_name: updated.full_name, preferred_locale: updated.preferred_locale });
      i18n.changeLanguage(updated.preferred_locale);
      toast.success(t('profileUpdated'));
    } catch {
      toast.error(t('failedToUpdateProfile'));
    } finally {
      setSaving(false);
    }
  }

  async function handlePasswordChange(e: React.FormEvent) {
    e.preventDefault();
    if (newPw !== confirmPw) {
      toast.error(t('common:passwordsDoNotMatch'));
      return;
    }
    if (newPw.length < 8) {
      toast.error(t('common:passwordMinLength'));
      return;
    }
    try {
      setChangingPw(true);
      await changePassword(currentPw, newPw);
      toast.success(t('passwordChanged'));
      setCurrentPw('');
      setNewPw('');
      setConfirmPw('');
    } catch (err: unknown) {
      // axios wraps the response body under err.response.data. Falling through
      // to err.message loses the backend detail and shows the generic
      // "Request failed with status code 400" to the user.
      const backendDetail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      const msg = backendDetail || (err instanceof Error ? err.message : t('failedToChangePassword'));
      console.error('[SettingsPage] change password failed:', err);
      toast.error(msg);
    } finally {
      setChangingPw(false);
    }
  }

  if (loading) {
    return <div className={styles.loading}>{t('loadingSettings')}</div>;
  }

  return (
    <div className={styles.page}>
      <h2 className={styles.title}>{t('settingsTitle')}</h2>

      {/* Account info (read-only) */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>{t('account')}</h3>
        <div className={styles.infoGrid}>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>{t('emailLabel')}</span>
            <span className={styles.infoValue}>{profile?.email}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>{t('roleLabel')}</span>
            <span className={styles.roleBadge}>{profile?.role}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>{t('memberSince')}</span>
            <span className={styles.infoValue}>
              {profile?.created_at
                ? new Date(profile.created_at).toLocaleDateString('fr-CH')
                : '\u2014'}
            </span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>{t('lastLogin')}</span>
            <span className={styles.infoValue}>
              {profile?.last_login
                ? new Date(profile.last_login).toLocaleString('fr-CH')
                : '\u2014'}
            </span>
          </div>
        </div>
      </section>

      {/* Profile form */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>{t('profile')}</h3>
        <form className={styles.form} onSubmit={handleProfileSave}>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('common:fullName')}</span>
            <input
              className={styles.input}
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
            />
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('language')}</span>
            <select
              className={styles.input}
              value={locale}
              onChange={(e) => setLocale(e.target.value)}
            >
              {LOCALE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <div className={styles.formActions}>
            <button className={styles.primaryBtn} type="submit" disabled={saving}>
              {saving ? t('saving') : t('saveChanges')}
            </button>
          </div>
        </form>
      </section>

      {/* Password change */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>{t('changePassword')}</h3>
        <form className={styles.form} onSubmit={handlePasswordChange}>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('currentPassword')}</span>
            <input
              className={styles.input}
              type="password"
              value={currentPw}
              onChange={(e) => setCurrentPw(e.target.value)}
              required
            />
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('newPassword')}</span>
            <input
              className={styles.input}
              type="password"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              required
              minLength={8}
            />
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('confirmNewPassword')}</span>
            <input
              className={styles.input}
              type="password"
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
              required
              minLength={8}
            />
          </label>
          <div className={styles.formActions}>
            <button className={styles.primaryBtn} type="submit" disabled={changingPw}>
              {changingPw ? t('changing') : t('changePassword')}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
